import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Box, Typography, Button, CircularProgress, Alert, Container, Input, Grid, Paper
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';

// This page will be linked from the background check request email.
// It allows the person to upload their background check document.

function BackgroundCheckUploadPage() {
  const location = useLocation();
  
  // Extract id and token from query parameters
  const searchParams = new URLSearchParams(location.search);
  const id = searchParams.get('id'); // This is the background check ID
  const token = searchParams.get('token'); // Upload token from email
  const [file, setFile] = useState(null);
  const [uploadStatus, setUploadStatus] = useState(null);
  const [uploadMessage, setUploadMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [generalError, setGeneralError] = useState(null);
  const [generalSuccess, setGeneralSuccess] = useState(null);

  const [backgroundCheckDetails, setBackgroundCheckDetails] = useState(null); // State to store background check details

  useEffect(() => {
    // Validate required query parameters
    if (!id || !token) {
      setGeneralError('Missing background check ID or token. Please use the link provided in your email.');
      return;
    }

    const fetchBackgroundCheckDetails = async () => {
      try {
        // Create a new endpoint for public access to background check details using token
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/background-checks/${id}/details-for-upload?token=${token}`);
        if (!response.ok) throw new Error('Failed to fetch background check details');
        const data = await response.json();
        setBackgroundCheckDetails(data); // Store background check details
      } catch (error) {
        setGeneralError(error.message || 'Failed to load background check details.');
      }
    };
    
    fetchBackgroundCheckDetails();
  }, [id, token]);


  const handleFileChange = (event) => {
    if (event.target.files && event.target.files[0]) {
      setFile(event.target.files[0]);
      setUploadStatus(null); // Reset status on new file selection
      setUploadMessage('');
    } else {
      setFile(null);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      setUploadMessage('Please select a file to upload.');
      setUploadStatus('error');
      return;
    }

    setUploadStatus('uploading');
    setUploadMessage('Uploading...');
    setLoading(true);

    const formData = new FormData();
    formData.append('file', file); // Match backend multer field name
    formData.append('id', id); // Pass background check ID
    formData.append('token', token); // Pass upload token

    try {
      // Use the correct background check upload endpoint
      const uploadUrl = `${process.env.REACT_APP_API_URL}/api/background-checks/upload`;

      const response = await fetch(uploadUrl, {
        method: 'POST',
        // No 'Content-Type' header needed for FormData
        body: formData,
      });

      const result = await response.json();

      if (response.ok) {
        setUploadStatus('success');
        setUploadMessage(result.message || 'Upload successful!');
        setGeneralSuccess('Background check document uploaded successfully!');
      } else {
        setUploadStatus('error');
        setUploadMessage(result.message || 'File upload failed.');
      }

    } catch (error) {
      console.error('Error uploading background check:', error);
      setUploadStatus('error');
      setUploadMessage('An error occurred during upload.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="md" sx={{ mt: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h5" gutterBottom textAlign="center">
          Background Check Upload
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }} textAlign="center">
          Please upload the background check document for: {backgroundCheckDetails?.personName || 'the requested person'}
        </Typography>
        
        {/* Show validation error if id or token is missing */}
        {(!id || !token) && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Invalid or missing background check ID or token. Please use the link provided in your email.
          </Alert>
        )}

        {generalError && <Alert severity="error" sx={{ mb: 2 }}>{generalError}</Alert>}
        {generalSuccess && <Alert severity="success" sx={{ mb: 2 }}>{generalSuccess}</Alert>}

        {/* Only show the form if we have valid id and token */}
        {id && token && (
          <Box sx={{ mt: 4 }}>
            <Grid container spacing={3} justifyContent="center">
              <Grid item xs={12} md={8}>
                <Box sx={{ border: '2px dashed #ccc', borderRadius: 2, p: 4, textAlign: 'center' }}>
                  <Input
                    type="file"
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                    onChange={handleFileChange}
                    sx={{ display: 'none' }}
                    id="background-check-file-upload"
                  />
                  <label htmlFor="background-check-file-upload">
                    <Button
                      variant="outlined"
                      component="span"
                      startIcon={<CloudUploadIcon />}
                      size="large"
                      sx={{ mb: 2 }}
                    >
                      {file ? file.name : 'Select Background Check Document'}
                    </Button>
                  </label>
                  
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Accepted formats: PDF, DOC, DOCX, JPG, JPEG, PNG (Max 10MB)
                  </Typography>
                  
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleUpload}
                    disabled={!file || uploadStatus === 'uploading' || uploadStatus === 'success'}
                    size="large"
                    sx={{ minWidth: 120 }}
                  >
                    {uploadStatus === 'uploading' ? <CircularProgress size={24} color="inherit" /> : 'Upload Document'}
                  </Button>
                </Box>
                
                {uploadStatus && (
                  <Alert
                    severity={uploadStatus === 'uploading' ? 'info' : uploadStatus}
                    sx={{ mt: 2 }}
                  >
                    {uploadMessage}
                  </Alert>
                )}
              </Grid>
            </Grid>
          </Box>
        )}

      </Paper>
    </Container>
  );
}

export default BackgroundCheckUploadPage;